import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ExcelTable } from "@/components/ExcelTable";
import { RapBadge, RapportinoDetailDialog } from "@/components/RapportinoDetail";
import { ClipboardList, FileSpreadsheet, FileText, Pencil, AlertTriangle, Check, History, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (n) => "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function Rapportini() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [hourAnoms, setHourAnoms] = useState([]);
  const [hourAnomsDone, setHourAnomsDone] = useState([]);
  const [showStorico, setShowStorico] = useState(false);
  const [storicoName, setStoricoName] = useState("");
  const [storicoDate, setStoricoDate] = useState("");

  const refresh = () => {
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    return api.get("/rapportini", { params }).then((r) => setItems(r.data)).catch(() => {});
  };
  const rows = useMemo(() => items.map((r) => ({
    ...r,
    date_str: fmtDate(r.date),
    status_label: r.status === "trasmesso" ? "Trasmesso" : "Bozza",
  })), [items]);
  const loadAnoms = () => {
    api.get("/hour-anomalies?status=aperta").then((r) => setHourAnoms(r.data)).catch(() => {});
    api.get("/hour-anomalies?status=confermata").then((r) => setHourAnomsDone(r.data)).catch(() => {});
  };
  useEffect(() => { refresh(); }, [from, to]);
  useEffect(() => { loadAnoms(); }, []);
  usePolling(refresh, 15000);
  usePolling(loadAnoms, 20000);

  const confirmAnom = async (id) => {
    try { await api.post(`/hour-anomalies/${id}/confirm`); toast.success("Anomalia confermata"); loadAnoms(); }
    catch (e) { toast.error("Errore"); }
  };


  const exportRap = async (format) => {
    try {
      const params = { format };
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const res = await api.get("/rapportini/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `rapportini.${format === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore durante l'export"); }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="flex flex-wrap items-center gap-2 font-heading text-3xl font-extrabold tracking-tight text-slate-900">
            Rapportini
            {hourAnoms.length > 0 && (
              <span data-testid="open-anom-count" className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-2.5 py-1 text-sm font-semibold text-white">
                <AlertTriangle className="h-3.5 w-3.5" />{hourAnoms.length} anomalie ore
              </span>
            )}
          </h1>
          <p className="mt-1 text-sm text-slate-500">Rapportini trasmessi dagli operatori. Clicca una riga per il dettaglio; sblocca per renderlo modificabile.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs text-slate-500">Dal</Label><Input type="date" data-testid="rap-from" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="mt-1.5 w-40" /></div>
          <div><Label className="text-xs text-slate-500">Al</Label><Input type="date" data-testid="rap-to" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="mt-1.5 w-40" /></div>
          {(from || to) && <Button variant="ghost" size="sm" onClick={() => { setFrom(""); setTo(""); }} data-testid="rap-reset-filter" className="rounded-md">Azzera</Button>}
          <Button variant="outline" size="sm" onClick={() => exportRap("xlsx")} data-testid="rap-export-xlsx" className="rounded-md"><FileSpreadsheet className="mr-1.5 h-4 w-4" />Excel</Button>
          <Button variant="outline" size="sm" onClick={() => exportRap("pdf")} data-testid="rap-export-pdf" className="rounded-md"><FileText className="mr-1.5 h-4 w-4" />PDF</Button>
        </div>
      </div>
      {hourAnoms.length > 0 && (
        <Card className="mb-6 rounded-lg border-amber-200 bg-amber-50/50 p-4 shadow-sm" data-testid="hour-anomalies">
          <h2 className="mb-1 flex items-center gap-1.5 font-heading font-bold text-amber-800"><AlertTriangle className="h-4 w-4" />Anomalie ore ({hourAnoms.length})</h2>
          <p className="mb-3 text-xs text-amber-700">Dipendenti e mezzi con meno di 8 ore nella somma dei rapportini della giornata. Conferma se le ore sono corrette, oppure modifica il rapportino.</p>
          <div className="space-y-2">
            {hourAnoms.map((a) => (
              <div key={a.id} data-testid={`hour-anom-${a.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-white p-2.5">
                <div className="min-w-0">
                  <Badge variant="outline" className={a.subject_type === "vehicle" ? "border-slate-200 bg-slate-50 text-slate-600" : "border-blue-200 bg-blue-50 text-[#002FA7]"}>{a.subject_type === "vehicle" ? "Mezzo" : "Dipendente"}</Badge>
                  <span className="ml-2 font-medium text-slate-900">{a.subject_name}</span>
                  <span className="ml-2 text-sm text-slate-500">{fmtDate(a.date)} · {a.total_hours}h / 8h</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => confirmAnom(a.id)} data-testid={`confirm-anom-${a.id}`} className="rounded-md border-emerald-200 text-emerald-700 hover:bg-emerald-50"><Check className="mr-1.5 h-4 w-4" />Conferma</Button>
                  <Button size="sm" variant="outline" disabled={!a.rapportino_ids?.length} onClick={() => a.rapportino_ids?.length && navigate(`/rapportino/${a.rapportino_ids[0]}`)} data-testid={`modify-anom-${a.id}`} className="rounded-md"><Pencil className="mr-1.5 h-4 w-4" />Modifica</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      {hourAnomsDone.length > 0 && (
        <div className="mb-6" data-testid="hour-anomalies-history">
          <button onClick={() => setShowStorico((s) => !s)} data-testid="toggle-anom-history" className="mb-2 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
            <History className="h-4 w-4" /> Storico anomalie confermate ({hourAnomsDone.length})
            {showStorico ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {showStorico && (
            <Card className="rounded-lg border-slate-200 p-4 shadow-sm">
              <div className="mb-3 flex flex-wrap items-end gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Persona/Mezzo</Label>
                  <Input value={storicoName} onChange={(e) => setStoricoName(e.target.value)} placeholder="Cerca nome..." data-testid="storico-filter-name" className="mt-1 h-9 w-52" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Data</Label>
                  <Input type="date" value={storicoDate} onChange={(e) => setStoricoDate(e.target.value)} data-testid="storico-filter-date" className="mt-1 h-9 w-44" />
                </div>
                {(storicoName || storicoDate) && (
                  <Button size="sm" variant="ghost" onClick={() => { setStoricoName(""); setStoricoDate(""); }} data-testid="storico-filter-clear" className="h-9 text-slate-500">Azzera filtri</Button>
                )}
              </div>
              <div className="space-y-2">
                {(() => {
                  const list = hourAnomsDone.filter((a) =>
                    (!storicoName || (a.subject_name || "").toLowerCase().includes(storicoName.toLowerCase())) &&
                    (!storicoDate || a.date === storicoDate));
                  if (!list.length) return <p className="py-4 text-center text-sm text-slate-400" data-testid="storico-empty">Nessuna anomalia confermata con questi filtri.</p>;
                  return list.map((a) => (
                  <div key={a.id} data-testid={`hour-anom-done-${a.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-100 bg-slate-50/50 p-2.5">
                    <div className="min-w-0">
                      <Badge variant="outline" className={a.subject_type === "vehicle" ? "border-slate-200 bg-slate-50 text-slate-600" : "border-blue-200 bg-blue-50 text-[#002FA7]"}>{a.subject_type === "vehicle" ? "Mezzo" : "Dipendente"}</Badge>
                      <span className="ml-2 font-medium text-slate-900">{a.subject_name}</span>
                      <span className="ml-2 text-sm text-slate-500">{fmtDate(a.date)} · {a.total_hours}h / 8h</span>
                    </div>
                    <span className="text-xs text-slate-500" data-testid={`hour-anom-done-info-${a.id}`}>Confermata da {a.resolved_by || "—"}{a.resolved_at ? ` · ${fmtDateTime(a.resolved_at)}` : ""}</span>
                  </div>
                  ));
                })()}
              </div>
            </Card>
          )}
        </div>
      )}
      <Card className="rounded-lg border-slate-200 shadow-sm">
        <div className="p-4">
          <ExcelTable data={rows} testid="rapportini-table" rowTestId={(r) => `rap-row-${r.id}`} onRowClick={(r) => setDetail(r)}
            searchKeys={["operator_name", "commessa_name", "commessa_code"]} searchPlaceholder="Cerca rapportino..." initialSort={{ key: "date_str", dir: "desc" }}
            emptyIcon={ClipboardList} emptyText="Nessun rapportino"
            columns={[
              { key: "date_str", header: "Data", sortValue: (r) => new Date(r.date + "T00:00:00").getTime(), className: "text-slate-500", cell: (r) => r.date_str },
              { key: "operator_name", header: "Operatore", className: "font-medium text-slate-900", cell: (r) => r.operator_name },
              { key: "commessa_name", header: "Commessa", className: "text-slate-700", cell: (r) => r.commessa_name },
              { key: "hours", header: "Ore", type: "number", className: "text-slate-600", cell: (r) => `${r.hours} h` },
              { key: "revenue", header: "Ricavi", type: "number", align: "right", className: "text-right text-emerald-700 font-semibold", cell: (r) => fmtEur(r.revenue) },
              { key: "cost_total", header: "Costi", type: "number", align: "right", className: "text-right text-rose-700", cell: (r) => fmtEur(r.cost_total) },
              { key: "margin", header: "Margine", type: "number", align: "right", className: "text-right font-heading font-bold text-slate-900", cell: (r) => fmtEur(r.margin) },
              { key: "status_label", header: "Stato", cell: (r) => <div className="flex items-center gap-1.5"><RapBadge s={r.status} />{r.edit_requested && <Badge variant="outline" className="border-blue-200 bg-blue-50 text-[#002FA7]">Modifica richiesta</Badge>}</div> },
            ]} />
        </div>
      </Card>

      <RapportinoDetailDialog detail={detail} open={!!detail} onClose={() => setDetail(null)} onChanged={refresh} backLabel="Torna ad elenco rapportini" />
    </div>
  );
}
